<template>
  <page-metrics :metrics="metrics" />
</template>

<script setup>
import moment from 'moment';
import { onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import PageMetrics from '@/components/PageMetrics.vue';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { TIME_SERIES } from '@/utils/chart';
import { toPercentage } from '@/utils/common';
import { toMonthDescriptor } from '@/utils/date';

const { divisionId } = useDivisionSelection();
const bankingInfo = getBankingInfo();

const ALL_SNAPSHOT_METRICS = {
  track_users: {
    title: 'Budget views',
    value: undefined,
    backgroundColor: 'core-2-lighten-20',
  },
  stream_users: {
    title: `${bankingInfo.stream.label} views`,
    value: undefined,
    backgroundColor: 'brand-primary',
  },
  save_users: {
    title: `${bankingInfo.save.label} views`,
    value: undefined,
    backgroundColor: 'core-3',
  },
  hub_users: {
    title: 'Hub views',
    value: undefined,
    backgroundColor: 'core-4',
  },
  home_users: {
    title: 'Home views',
    value: undefined,
    backgroundColor: 'core-5',
  },
};

const metrics = ref({});

watch(divisionId, () => getMetrics());

const getMetrics = async () => {
  const { results } = await ApiClient.getAppFeaturesMetrics(TIME_SERIES.MONTH_TO_MONTH, divisionId.value);

  // `last_complete` is null until a period has fully elapsed; for a newly launched
  // client fall back to the last plotted period so tiles reflect the month on screen
  // rather than formatting the epoch (moment.unix(null)) as "January".
  const lastPlotted = result => result.calculated.last_complete ?? result.calculated.last;
  const uniqueUsers = lastPlotted(results.find(result => result.id === 'unique_users'));
  const monthDescriptor = toMonthDescriptor(moment.unix(lastPlotted(results.find(result => result.id === 'time'))));

  const enabledMetrics = {};
  results.forEach(result => {
    if (result.id in ALL_SNAPSHOT_METRICS) {
      enabledMetrics[result.id] = {
        ...ALL_SNAPSHOT_METRICS[result.id],
        title: `${ALL_SNAPSHOT_METRICS[result.id].title} ${monthDescriptor}`,
        value: lastPlotted(result),
        secondaryValue: toPercentage(lastPlotted(result) / uniqueUsers),
      };
    }
  });
  metrics.value = enabledMetrics;
};

onBeforeMount(() => getMetrics());
</script>
